import type { FaqItem, PricingConfig, PricingPlan } from '@/types/content'
import type { PublicPricingInfo, PublicPricingPlanInfo } from '@/lib/currentDataApi'

function formatAud(value: number): string {
  if (Number.isInteger(value)) return String(value)
  return value.toFixed(2).replace(/\.?0+$/, '')
}

function getDisplayMonthlyPrice(planInfo: PublicPricingPlanInfo): string | null {
  if (typeof planInfo.minPricePerMonthAud === 'number') return formatAud(planInfo.minPricePerMonthAud)
  if (
    typeof planInfo.pricePerUserPerMonthAud === 'number' &&
    typeof planInfo.minUsers === 'number'
  ) {
    return formatAud(planInfo.pricePerUserPerMonthAud * planInfo.minUsers)
  }
  return null
}

function upsertFeatureText(plan: PricingPlan, startsWith: string, nextValue: string): void {
  const target = plan.features.find((f) => f.text.toLowerCase().startsWith(startsWith.toLowerCase()))
  if (target) target.text = nextValue
}

function applyPlanApi(plan: PricingPlan, planInfo: PublicPricingPlanInfo): PricingPlan {
  const next = structuredClone(plan)
  if (planInfo.description) next.subtitle = planInfo.description

  const monthly = getDisplayMonthlyPrice(planInfo)
  if (monthly != null) {
    next.priceCurrency = '$'
    next.priceValue = monthly
    next.pricePostfix = 'month'
  }

  if (planInfo.billingPlanKey === 'free') {
    next.priceCaption = 'per user'
  } else if (monthly != null) {
    next.priceCaption = 'per dealership'
  }

  if (typeof planInfo.maxUsers === 'number') {
    upsertFeatureText(next, 'Up to ', `Up to ${planInfo.maxUsers} users per dealership`)
  }
  if (typeof planInfo.pricePerUserPerMonthAud === 'number' && typeof planInfo.minUsers === 'number') {
    upsertFeatureText(
      next,
      'Then $',
      `Then $${formatAud(planInfo.pricePerUserPerMonthAud)} per additional user`
    )
  }
  if (planInfo.documentsUnlimited === true) {
    upsertFeatureText(next, 'Unlimited Documents', 'Unlimited Documents')
  } else if (typeof planInfo.maxDocumentsPerMonth === 'number') {
    upsertFeatureText(
      next,
      '1000 Documents per month',
      `${planInfo.maxDocumentsPerMonth} Documents per month`
    )
    upsertFeatureText(
      next,
      '50 Documents per month',
      `${planInfo.maxDocumentsPerMonth} Documents per month`
    )
  }

  return next
}

export function mergePricingConfigWithPublicInfo(
  fallbackPricing: PricingConfig | undefined,
  pricingInfo: PublicPricingInfo
): PricingConfig | undefined {
  if (!fallbackPricing) return fallbackPricing

  const next = structuredClone(fallbackPricing)
  const byName: Record<string, string> = {
    starter: 'free',
    team: 'team',
    dealership: 'dealership',
    benchmark: 'benchmark',
  }

  next.plans = next.plans.map((plan) => {
    const key = byName[plan.name.trim().toLowerCase()]
    const info = key ? pricingInfo.plans[key] : undefined
    if (!info) return plan
    return applyPlanApi(plan, info)
  })

  const dealershipYearly = pricingInfo.plans.dealershipYearly
  if (dealershipYearly) {
    const dealershipPlan = next.plans.find((p) => p.name.trim().toLowerCase() === 'dealership')
    if (dealershipPlan) {
      const yearlyPrice = getDisplayMonthlyPrice(dealershipYearly)
      dealershipPlan.yearly = {
        ...(dealershipPlan.yearly ?? {}),
        subtitle: dealershipYearly.description ?? dealershipPlan.yearly?.subtitle,
        priceValue: yearlyPrice ?? dealershipPlan.yearly?.priceValue,
        pricePostfix: yearlyPrice != null ? 'month' : dealershipPlan.yearly?.pricePostfix,
        priceCaption: yearlyPrice != null ? 'Paid yearly' : dealershipPlan.yearly?.priceCaption,
      }
      dealershipPlan.yearlyAvailable = true
      if (typeof dealershipYearly.annualDiscountPercentage === 'number') {
        next.yearlyHint = `Save ${dealershipYearly.annualDiscountPercentage}%`
      }
    }
  }

  return next
}

function formatPerUserPerMonthText(value: number): string {
  return `$${formatAud(value)}/month/user`
}

export function mergeFaqWithPublicPricing(
  fallbackFaq: FaqItem[],
  pricingInfo: PublicPricingInfo
): FaqItem[] {
  const perUser = pricingInfo.currentPerUserPerMonthAud
  if (typeof perUser !== 'number') return fallbackFaq

  const nextText = formatPerUserPerMonthText(perUser)
  return fallbackFaq.map((item) => {
    if (!/how much does it cost\??/i.test(item.question)) return item
    const updatedAnswer = item.answer.replace(
      /(\*\*)?\$?\d+(?:\.\d+)?\s*\/\s*month\s*\/\s*user(\*\*)?/gi,
      `**${nextText}**`
    )
    return { ...item, answer: updatedAnswer }
  })
}
